import { useEffect, useRef, useState } from 'react';
import {
  Alert,
  Box,
  Chip,
  Drawer,
  IconButton,
  Typography,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { getSocket } from '../../api/socket';
import type { ImportJobState } from '../../api/mysqlImport';

interface Props {
  open:    boolean;
  jobId:   string;
  dbname:  string;
  onClose: () => void;
}

export function ImportLogDrawer({ open, jobId, dbname, onClose }: Props) {
  const [state, setState] = useState<ImportJobState | null>(null);
  const [lines, setLines] = useState<string[]>([]);
  const bottomRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!jobId) return;
    const socket = getSocket();
    socket.emit('mysql:import:subscribe', { jobId });

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const onState = (s: ImportJobState) => setState(s);
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const onLog   = ({ lines: l }: { lines: string[] }) => {
      setLines(l);
      setTimeout(() => bottomRef.current?.scrollIntoView({ behavior: 'smooth' }), 50);
    };

    socket.on(`mysql:import:state:${jobId}` as never, onState as never);
    socket.on(`mysql:import:log:${jobId}`   as never, onLog   as never);

    return () => {
      socket.emit('mysql:import:unsubscribe', { jobId });
      socket.off(`mysql:import:state:${jobId}` as never, onState as never);
      socket.off(`mysql:import:log:${jobId}`   as never, onLog   as never);
    };
  }, [jobId]);

  const statusColor =
    state?.status === 'succeeded' ? 'success' :
    state?.status === 'failed'    ? 'error'   :
    'default';

  const running = !state || state.status === 'running';

  return (
    <Drawer anchor="right" open={open} onClose={running ? undefined : onClose}>
      <Box sx={{ width: 560, display: 'flex', flexDirection: 'column', height: '100%' }}>
        {/* Header */}
        <Box sx={{ p: 2, display: 'flex', alignItems: 'center', gap: 1, borderBottom: 1, borderColor: 'divider' }}>
          <Typography variant="h6" sx={{ flex: 1 }}>Importing {dbname}</Typography>
          {state?.status && (
            <Chip label={state.status} color={statusColor} size="small" />
          )}
          <IconButton onClick={onClose} disabled={running}>
            <CloseIcon />
          </IconButton>
        </Box>

        {/* Log output */}
        <Box sx={{ flex: 1, overflow: 'auto', p: 2, fontFamily: 'monospace', fontSize: 13, background: '#111', color: '#e0e0e0' }}>
          {lines.length === 0 && running && (
            <Typography variant="caption" color="text.secondary">Waiting for import to start…</Typography>
          )}
          {lines.map((line, i) => (
            <div key={i}>{line}</div>
          ))}
          <div ref={bottomRef} />
        </Box>

        {/* Footer status */}
        {state?.status === 'failed' && (
          <Alert severity="error" sx={{ m: 2 }}>Import failed. Check log output above.</Alert>
        )}
        {state?.status === 'succeeded' && (
          <Alert severity="success" sx={{ m: 2 }}>Import completed successfully.</Alert>
        )}
      </Box>
    </Drawer>
  );
}
